import { makeAutoObservable, runInAction } from 'mobx';

import { authBetProcessor } from '@/lib/api';
import { getHistory } from '@/lib/api/bets';
import { logger } from '@/lib/api/logger';
import { timeout } from '@/helpers/timeout';

const queryParams = {
  settled: 'finished',
  unsettled: 'placed',
};
const DELAYTIME = 500;
export class ProfileMyBets {
  constructor(root) {
    makeAutoObservable(this);

    this.root = root;
  }

  hasBets = false;

  bets = [];

  //Bets for infinite scroll
  betsPack = { settled: [], unsettled: [] };
  //Meta for infinite scroll
  metaPack = { settled: {}, unsettled: {} };

  loading = false;

  isBetHistoryWidget = false;

  meta = {};

  activeTab = 'unsettled';

  cashoutDelay = {};

  cashoutErrors = {};

  cashoutUpdate = [];

  cashouting = [];

  setIsBetHistoryWidget = (bool = true) => {
    this.isBetHistoryWidget = bool;
  };

  resolveCashoutError = (id) => {
    const { [id]: propValue, ...rest } = this.cashoutErrors;
    this.cashoutErrors = { ...rest };
  };

  getBetIndex = (ticketId) => {
    return this.betsPack.unsettled.findIndex(({ id }) => id === ticketId);
  };

  findBet = (ticketId) => {
    return this.betsPack.unsettled[this.getBetIndex(ticketId)];
  };

  updateBetCashout = (ticketId, newProps) => {
    const betIndex = this.getBetIndex(ticketId);
    const bet = this.betsPack.unsettled[betIndex];

    if (bet) {
      this.betsPack.unsettled[betIndex] = {
        ...bet,
        cashout: { ...bet.cashout, ...newProps },
      };
    }
  };

  cashoutBet = async (ticketId) => {
    this.cashouting = [...this.cashouting, ticketId];
    try {
      const bet = this.findBet(ticketId);
      if (!bet) {
        throw 'error';
      }
      this.updateBetCashout(ticketId, { status: 'pending' });
      const { cashout: { amount } = {} } = bet;
      const { data } = await authBetProcessor.post(`/cashout/tickets/${ticketId}`, { amount });
      if (data) {
        const { cashoutDelay, error_code } = data.data;
        if (error_code) {
          throw {
            response: {
              data: {
                message: error_code,
              },
            },
          };
        }
        this.cashoutDelay[ticketId] = cashoutDelay + 1;
        await timeout(cashoutDelay + 1);

        const {
          data: { data: ticket },
        } = await authBetProcessor.get(`/cashout/results/${ticketId}`);

        delete this.cashoutDelay[ticketId];
        if (ticket.error_code) {
          throw {
            response: {
              data: {
                message: ticket.error_code,
              },
            },
          };
        }

        this.updateBetCashout(ticketId, { status: 'done' });
        this.cashouting = this.cashouting.filter((id) => id !== ticketId);
        this.root.profile.updateBalance();
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        this.cashoutErrors[ticketId] = error.response.data.message;
      }
      this.updateBetCashout(ticketId, { status: 'error' });
      this.cashouting = this.cashouting.filter((id) => id !== ticketId);
    }
  };

  setActiveTab = (tab = 'unsettled') => {
    this.activeTab = tab;
  };

  getHistory = async (activeTab, page = 1) => {
    this.loading = true;
    const startTime = new Date();
    const { language } = this.root.ui;

    try {
      const { data } = await getHistory({
        language,
        page,
        status: queryParams[activeTab],
      });
      if (data) {
        runInAction(() => {
          const responseTime = new Date() - startTime;
          if (responseTime < DELAYTIME) {
            setTimeout(() => {
              this.setNewData(data);
            }, DELAYTIME - responseTime);
          } else {
            this.setNewData(data);
          }
        });
      } else {
        this.loading = false;
      }
    } catch (error) {
      this.loading = false;
    }
  };

  getCashoutById = async (ticketId) => {
    if (!this.cashouting[ticketId] && !this.cashoutUpdate[ticketId]) {
      const bet = this.findBet(ticketId);
      this.cashoutUpdate = [...this.cashoutUpdate, ticketId];
      try {
        if (!bet) {
          throw 'error';
        }
        const {
          data: { data },
        } = await authBetProcessor.get(`/cashout/tickets/${ticketId}`);
        this.updateBetCashout(ticketId, { ...data, status: 'initial' });
        this.resolveCashoutError(ticketId);
        this.cashoutUpdate = this.cashoutUpdate.filter((id) => id !== ticketId);
      } catch (error) {
        this.cashoutErrors[ticketId] = '...';
        this.cashoutUpdate = this.cashoutUpdate.filter((id) => id !== ticketId);
      }
    }
  };

  getCashoutData = async (data, type) => {
    if (type === 'unsettled') {
      const { data: list = [] } = data;
      try {
        const responses = await Promise.all(
          list.map(({ id }) => authBetProcessor.get(`/cashout/tickets/${id}`)),
        );
        const listWithCashout = list.map((d, i) => ({
          ...d,
          cashout: {
            ...responses[i].data.data,
            status: 'initial',
          },
        }));
        await this.setNewDataPack({ ...data, data: listWithCashout }, type);
        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    } else {
      await this.setNewDataPack(data, type);
      this.loading = false;
    }
  };

  getHistoryPack = async (type, page = 1, size = 25) => {
    if (this.loading) {
      return;
    }
    const { language } = this.root.ui;
    this.loading = true;
    const startTime = new Date();

    try {
      const { data } = await getHistory({
        language,
        page,
        status: queryParams[type],
      });

      if (data) {
        // ? for cases, when backend sends currency in uppercase we align it to lowercase for consistency
        const normalisedData = data?.data?.map((item) => ({
          ...item,
          currency: typeof item.currency === 'string' ? item.currency.toLowerCase() : item.currency,
        }));

        data.data = normalisedData;

        runInAction(() => {
          const responseTime = new Date() - startTime;
          if (responseTime < DELAYTIME) {
            setTimeout(() => {
              this.getCashoutData(data, type);
            }, DELAYTIME - responseTime);
          } else {
            this.getCashoutData(data, type);
          }
        });
      } else {
        this.loading = false;
      }
    } catch (error) {
      logger.error(`Get 'bet history' failed`, {
        error,
      });
    }
  };

  async setNewDataPack(data, type) {
    this.betsPack[type] = Object.values(
      [...this.betsPack[type], ...data.data].reduce(
        (acc, curr) => ({ ...acc, [curr.id]: curr }),
        {},
      ),
    );

    this.metaPack[type] = Object.keys(data.meta).reduce((acc, curr) => {
      if (curr === 'totalRows') {
        acc.total = data.meta[curr];
      }
      acc[curr] = data.meta[curr];
      return acc;
    }, {});
    this.loading = false;
  }

  setNewData(data) {
    this.bets.replace(data.data);
    this.meta = data.meta;
    this.loading = false;
  }

  resetBetsPacks = () => {
    this.betsPack = { settled: [], unsettled: [] };
    this.metaPack = { settled: {}, unsettled: {} };
  };

  deleteTicket = (ticketId, type) => {
    this.betsPack[type] = this.betsPack[type].filter(({ id }) => id !== ticketId);
    this.metaPack[type].total = this.metaPack[type].total - 1;
  };
}
